import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import lodashGet from "lodash/get";

import { makeStyles } from "@material-ui/styles";
import Divider from "@material-ui/core/Divider";
import Card from "@material-ui/core/Card";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import MuiButton from "@material-ui/core/Button";
import Alert from "@material-ui/lab/Alert";

import ImageEye from "@material-ui/icons/RemoveRedEye";
import ContentCreate from "@material-ui/icons/Create";
import DeleteIcon from "@material-ui/icons/Delete";
import ActionPrint from "@material-ui/icons/Print";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { PageHeader, PageFooter, showNotification, translate, Button, BackButton } from "jazasoft";

import Table from "../../../components/Table";
import MuiTable from "../../../components/MuiTable";
import CardHeader from "../../../components/CardHeader";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSetting, useTime, useTenant } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { SpreadingType, Entity, GroupType, LayType, MarkerType, OState } from "../../../utils/types";
import { getDistinctValues, isEmpty, isTrue, toFixed, toFixedSigned } from "../../../utils/helpers";
import { getEffectiveExtraPercent, findSizeBreakupList, getCutOrderFlowInfoList } from "../../../helpers/orderHelper";
import { getRollNo } from "../../../helpers/warehouseHelper";

import { getSmallestML, isFeedbackCompleted, printBundleSheet, printGarmentRecuttingReport } from "../feedbackHelper";

import ManualMarkerDialog from "./ManualMarkerDialog";
import { SET_LAY } from "../layslip/layReducer";
import { getGroupName } from "../../grouping/groupingHelper";
import LayDialog from "./LayDialog";
import { DEFAULT_SEPARATOR, LayState, LotType } from "../../../constants";

const csv = (rows, field) =>
  isEmpty(rows) || isEmpty(field) ? null : getDistinctValues(rows.map(row => row[field]).filter(e => !isEmpty(e))).join(", ");

const poColumns = [
  { dataKey: "poRef", title: "PO Ref" },
  { dataKey: "customerPoRef", title: "Customer PO Ref" },
  { dataKey: "customerName", title: "Customer" },
  { dataKey: "seasonName", title: "Season" },
  { dataKey: "customField1", title: "Custom Field 1" },
  { dataKey: "customField2", title: "Custom Field 2" },
  { dataKey: "customField3", title: "Custom Field 3" },
  { dataKey: "desc", title: "Description" }
];

const columnsRoll = [
  { dataKey: "serialNo", title: "Sl. No." },
  { dataKey: "rollNo", title: "Roll Number" },
  { dataKey: "rollType", title: "Roll Type" },
  { dataKey: "groupName", title: "Group", length: 100 },
  { dataKey: "color", title: "Style Color" },
  { dataKey: "shade", title: "Shade" },
  { dataKey: "length", title: "Length" },
  { dataKey: "width", title: "Width" },
  { dataKey: "reCutGarment", title: "Used (Recutting)" },
  { dataKey: "available", title: "Available" }
];

const useStyles = makeStyles({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0.5em"
  },
  button: {
    marginRight: "1em"
  }
});

const GarmentRejectionView = props => {
  const classes = useStyles();
  const { id, translate } = props;
  const routeState = props.location?.state;
  const tenant = useTenant();

  const [version, setVersion] = React.useState(0);
  const [selectedKey, setSelectedKey] = React.useState(null);
  const [styleColorTabIdx, setStyleColorTabIdx] = React.useState(0);
  const [editing, setEditing] = React.useState(false);
  const [dialogState, setDialogState] = React.useState({ open: false });

  const dispatch = useDispatch();
  const i18nMessages = useSelector(state => state?.i18n?.messages || {});
  const history = useHistory();
  const { utc_offset } = useTime();
  const [mutate, { loading: busy }] = useMutation();
  const [download] = useMutation({ rawResponse: true });
  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ORDERS);
  const deviationTypeStr = useSetting(SettingKeys.LAYSLIP_DEVIATION_CAUSES);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FLOW_INFO);
  const messages = useSelector(state => state?.i18n?.messages || {});

  const deviations = isEmpty(deviationTypeStr) ? [] : JSON.parse(deviationTypeStr);
  const deviationTypes = deviations.reduce((acc, el) => ({ ...acc, [el.key]: el.value }), {});

  const { data: order = {} } = useQuery({ url: `orders/${id}`, params: { page: "cutPlan" }, version });

  const poInfoColumns = poColumns.filter(c => orderFields.includes(c.dataKey));

  const poInfoRows = useDeepMemo(() => {
    if (isEmpty(order)) return [];
    return [{ ...order, customerName: order?.customer?.name, seasonName: order?.season?.name }];
  }, [order]);

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    let rowList = [];

    rowList = order.orderFlowList
      .filter(flow => flow.type === "CUTPLAN" && isEmpty(flow.parentId))
      .filter(flow => flow.stateValue >= OState.FINAL_GROUPING_COMPLETED.stateValue)
      .flatMap(orderFlow => {
        const flowInfoList = getCutOrderFlowInfoList(order, orderFlow).sort((a, b) => a.serialNo - b.serialNo) || [];
        const parentIds = getDistinctValues(flowInfoList.map(fi => fi.parentId).filter(e => !isEmpty(e)));
        // Simple: No Hierarchy
        if (isEmpty(parentIds)) {
          const children = flowInfoList.flatMap(flowInfo =>
            flowInfo.partList
              .sort((a, b) => a.serial - b.serial)
              .map(part => {
                const childOrderFlow = order.orderFlowList.find(
                  of => of.type === "CUTPLAN" && of.parentId === orderFlow.id && of.part === part.partName
                );
                return { ...flowInfo, part, orderFlow: childOrderFlow || orderFlow };
              })
          );
          if (children.length === 1) {
            return [
              {
                orderFlow,
                flowInfo: flowInfoList[0],
                part: flowInfoList[0]?.partList[0],
                flowInfoList: [],
                key: `${orderFlow.id}`,
                cutOrderId: orderFlow.id
              }
            ];
          } else {
            return [
              { orderFlow, key: `${orderFlow.id}`, flowInfoList, cutOrderId: orderFlow.id },
              ...children.map(flowInfo => ({
                flowInfo,
                orderFlow: flowInfo.orderFlow,
                part: flowInfo.part,
                key: `${flowInfo.orderFlow?.id}-${flowInfo.id}-${flowInfo.part?.id}`,
                parentKey: `${orderFlow.id}`
              }))
            ];
          }
        } else {
          const partMap = flowInfoList.flatMap(c => c.partList || []).reduce((acc, part) => ({ ...acc, [part.partName]: part }), {});
          const partList = Object.values(partMap);

          const parent = order.flowInfoList.find(fi => parentIds.includes(fi.id));
          const children = flowInfoList.flatMap(flowInfo => order.flowInfoList.filter(fi => fi.parentId === flowInfo.id));
          if (partList.length === 1) {
            // One level Hierarchy
            if (isEmpty(children)) {
              // Set Product
              if (isTrue(parent?.isSetParent)) {
              }
              // Multi-Destination
              else {
                const part = partList[0];
                if (flowInfoList.length === 1) {
                  return [{ orderFlow, flowInfo: flowInfoList[0], part, key: `${orderFlow.id}`, cutOrderId: orderFlow.id }];
                } else {
                  return [
                    { orderFlow, key: `${orderFlow.id}`, flowInfoList, part, cutOrderId: orderFlow.id },
                    ...flowInfoList.map(flowInfo => ({
                      flowInfo,
                      parentOrderFlow: orderFlow,
                      orderFlow,
                      part,
                      key: `${orderFlow?.id}-${flowInfo.id}-${part?.id}`,
                      parentKey: `${orderFlow.id}`
                    }))
                  ];
                }
              }
            }
          }
        }

        return [];
      })
      .map(({ orderFlow, flowInfo, flowInfoList, part, key, parentKey, cutOrderId }) => {
        if (!isEmpty(flowInfo)) {
          let result = {
            ...flowInfo,
            cutOrderId,
            key,
            parentKey,
            orderFlow,
            flowInfo,
            part,
            partName: isEmpty(part) ? "" : `${part?.partName}${isEmpty(part?.placement) ? "" : ` - ${part?.placement}`}`,
            productName: flowInfo?.product?.name,
            styleColor: flowInfo.color,
            lotValue: `${orderFlow.lotType === LotType.BULK ? "Lot" : orderFlow.lotType} ${orderFlow.lot}`,
            status: orderFlow.state
          };

          return result;
        } else {
          const orderQty = flowInfoList.reduce((acc, fi) => acc + fi.orderQty, 0);
          const extra = getEffectiveExtraPercent(flowInfoList);
          const bomCu = part?.bomCu || 0;

          return {
            cutOrderId,
            orderFlow,
            key,
            parentKey,
            productName: flowInfoList[0]?.product?.name,
            style: orderFlow.styles,
            styleColor: orderFlow.colors,
            partName: orderFlow.part,
            bomCu,
            lotValue: `${orderFlow.lotType === LotType.BULK ? "Lot" : orderFlow.lotType} ${orderFlow.lot}`,
            orderQty,
            extra,
            flowRef: csv(flowInfoList, "flowRef"),
            customerFlowRef: csv(flowInfoList, "customerFlowRef"),
            inseam: getDistinctValues(flowInfoList.map(fi => fi.inseam).filter(e => !isEmpty(e)))?.join(", "),
            destination: getDistinctValues(flowInfoList.map(fi => fi.destination).filter(e => !isEmpty(e)))?.join(", "),
            deliveryDate: getDistinctValues(flowInfoList.map(fi => fi.deliveryDate).filter(e => !isEmpty(e)))?.join(", "),

            status: orderFlow.state
          };
        }
      });

    return rowList;
  }, [order, utc_offset]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "flowRef", title: "Flow Ref.", render: value => value || "-" },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: value => value || "-" },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true } },
      { dataKey: "style", title: "Style", filterOptions: { filter: true } },
      { dataKey: "styleColor", title: "Style Color", filterOptions: { filter: true } },
      { dataKey: "partName", title: "Part" },
      { dataKey: "lotValue", title: "Lot" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "extra", title: "Extra (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      { dataKey: "inseam", title: "Inseam", filterOptions: { filter: true } },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true } },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true } },
      { dataKey: "status", title: "Status", filterOptions: { filter: true }, length: 100 }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }

    return cols;
  }, [flowInfoRows]);

  useDeepEffect(() => {
    if (!isEmpty(routeState?.selectedKey)) {
      setSelectedKey(routeState?.selectedKey);
    } else if (!isEmpty(flowInfoRows)) {
      setSelectedKey(flowInfoRows[0].key);
    }
  }, [flowInfoRows.length, routeState]);

  const onRowClick = row => {
    if (isEmpty(row.parentKey)) {
      setSelectedKey(row.key);
    }
  };

  const cutOrder = flowInfoRows.find(fi => fi.key === selectedKey);
  const orderFlow = cutOrder?.orderFlow || {};

  const { data: rollInfoList = [] } = useQuery(
    !isEmpty(orderFlow) && {
      url: `rolls`,
      params: { search: `cutOrderFlow.id==${orderFlow.id};(hasChild=='NULL',hasChild=='FALSE')`, size: 100000 },
      version
    }
  );
  const { data: layslip = {} } = useQuery(!isEmpty(orderFlow) && { url: `orders/${id}/lays`, params: { flowId: orderFlow.id }, version });

  const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);

  const styleCount = orderFlow?.styleList?.length;
  const styleColorList = orderFlow?.styleColorList || [];
  const styleColor = styleColorList.length > 1 ? styleColorList[styleColorTabIdx] : styleColorList[0];

  const defaultExpanded = flowInfoRows.filter(fi => isEmpty(fi.parentKey)).length === 1;

  const saveOrUpdateMarkers = (markers, onActionComplete) => {
    if (isEmpty(orderFlow)) return;

    const options = {
      url: `orders/${id}/markers`,
      method: "put",
      data: { markers, manual: true, reCutting: true },
      params: { flowId: orderFlow.id, action: "re_cutting" }
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Markers updated successfully."));
        setVersion(v => v + 1);
        setEditing(false);
      }
    });
  };

  //** Prepare Size Breakup Columns for View mode **/
  const { columnsCutSummary, headerRows } = useDeepMemo(() => {
    if (isEmpty(order) || isEmpty(orderFlow)) return { columnsCutSummary: [], headerRows: [] };

    const styleColorList = orderFlow.styleColorList;
    const sizeBreakupList = findSizeBreakupList(orderFlow, order);
    const sizeGroups = getDistinctValues(sizeBreakupList.sort((a, b) => a.serialNo - b.serialNo).map(sb => sb.sizeGroup));
    const styleCount = orderFlow?.styleList?.length;
    const styleColorIdx = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});
    const groupedCutPlan = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

    let groupRow = {};
    let sizeGroup;
    const sizeList = sizeBreakupList.map((sb, idx) => {
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sb.sizeKey] = sb.sizeGroup;
      }
      return {
        dataKey: sb.sizeKey,
        title: sb.size,
        align: "center",
        styleColorIdx: sizeGroups.length > 1 ? styleColorIdx[sb.sizeGroup] : undefined,
        leftBorder,
        rightBorder: idx === sizeBreakupList.length - 1,
        colSpan: ({ column, row, rowIdx }) =>
          !groupedCutPlan || rowIdx === 1
            ? 1
            : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sizeBreakupList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
      };
    });

    const rowCount = orderFlow.lot + 2;
    let columns = [
      {
        dataKey: "styleColor",
        title: styleCount > 1 ? "Style" : "Color",
        rowSpan: ({ rowIdx }) => (rowIdx % rowCount === 0 ? rowCount : 0)
      },
      { dataKey: "label", title: "Label" },
      ...sizeList,
      { dataKey: "total", title: "Total", leftBorder: true }
    ];
    if (styleColorList.length === 1) {
      columns = columns.filter(c => c.dataKey !== "styleColor");
    }

    columns = columns.map(c => ({ ...c, align: "center" }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    } else {
      headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    }

    return { columnsCutSummary: columns, headerRows };
  }, [order, orderFlow, flowInfoList]);

  //** Prepare Size Breakup Rows for View mode **/
  const rowsCutSummary = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(orderFlow)) return [];
    const flowInfoList = getCutOrderFlowInfoList(order, orderFlow).sort((a, b) => a.serialNo - b.serialNo) || [];
    const sbMap = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []).reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sizeBreakupList = Object.values(sbMap);

    const sizeList = sizeBreakupList.map(sb => ({ dataKey: sb.sizeKey, title: sb.size }));

    const approvedOrderCutQtyMap = JSON.parse(orderFlow.orderConfig.approvedOrderCutQty || "{}");
    const reCutGarmentQtyMap = JSON.parse(orderFlow.orderConfig.reCutGarmentQty || "{}");
    const layList = Object.keys(layslip).flatMap(key => layslip[key]);
    const rowList = layList.flatMap(lay => lay.rowList.map(row => ({ ...row, marker: lay.marker, colorShade: lay.colorShade })));

    let rows = [];

    // Order Qty
    const orderQty = sizeList.reduce((acc, { dataKey }) => {
      const qty = sizeBreakupList.filter(sb => sb.sizeKey === dataKey).reduce((acc, sb) => acc + sb.qty, 0);
      return { ...acc, [dataKey]: qty };
    }, {});
    let totalOrderQty = Object.keys(orderQty).reduce((acc, key) => acc + orderQty[key], 0);
    rows.push({ ...orderQty, styleColor, total: totalOrderQty, label: "Order Qty" });

    // Approved Qty
    const approvedQty = approvedOrderCutQtyMap[styleColor] || {};
    let totalApprovedQty = Object.keys(approvedQty).reduce((acc, key) => acc + approvedQty[key], 0);
    rows.push({ ...approvedQty, styleColor, total: totalApprovedQty, label: "Approved Qty" });

    // Cut Qty
    const cutQty = sizeList.reduce((acc, { dataKey }) => {
      const qty = rowList
        .filter(row => row.completed && row.styleColor === styleColor && row.colorShade !== LayType.RECUTTING)
        .reduce((acc, row) => {
          const sizeRatio = row.marker.sizeRatioList.find(sr => sr.sizeKey === dataKey);
          const plies = SpreadingType.FACE_ONE_WAY !== row.marker.spreadingType ? row.actualPly / 2 : row.actualPly;
          const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
          return acc + qty;
        }, 0);
      return { ...acc, [dataKey]: qty };
    }, {});
    let totalCutQty = Object.keys(cutQty).reduce((acc, key) => acc + cutQty[key], 0);
    rows.push({ ...cutQty, styleColor, total: totalCutQty, label: "Cut Qty" });

    // Shortage/Excess
    const shortageExcessQty = sizeList.reduce((acc, { dataKey }) => {
      const qty = cutQty[dataKey] - approvedQty[dataKey];
      return { ...acc, [dataKey]: qty };
    }, {});
    let totalShortageExcessQty = Object.keys(shortageExcessQty).reduce((acc, key) => acc + shortageExcessQty[key], 0);
    rows.push({ ...shortageExcessQty, styleColor, total: totalShortageExcessQty, label: "Shortage/Excess" });

    // Garment Recutting
    const reCutGarmentQty = reCutGarmentQtyMap[styleColor] || {};
    const reCutQty = sizeList.reduce((acc, { dataKey }) => {
      let qty = rowList
        .filter(row => row.completed && row.styleColor === styleColor && row.colorShade === LayType.RECUTTING)
        .reduce((acc, row) => {
          const sizeRatio = row.marker.sizeRatioList.find(sr => sr.sizeKey === dataKey);
          const plies = SpreadingType.FACE_ONE_WAY !== row.marker.spreadingType ? row.actualPly / 2 : row.actualPly;
          const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
          return acc + qty;
        }, 0);
      qty = qty + (reCutGarmentQty[dataKey] || 0);
      return { ...acc, [dataKey]: qty };
    }, {});
    let totalReCutQty = Object.keys(reCutQty).reduce((acc, key) => acc + reCutQty[key], 0);
    rows.push({ ...reCutQty, styleColor, total: totalReCutQty, label: "Garment Recutting" });

    return rows;
  }, [order, orderFlow, flowInfoList, layslip]);

  const onViewClick = lay => {
    setDialogState({ open: true, lay });
  };

  const onEditClick = (lay, layList) => {
    if (lay.state === LayState.COMPLETED) {
      dispatch(showNotification("Feedback cannot be updated for completed lay."));
      return;
    }
    dispatch({ type: SET_LAY, payload: { lay, layList, orderFlow, layTab: LayType.RECUTTING, orderId: id } });
    history.push(`/feedback/garmentRecutting/${id}/view/lay`, { selectedKey });
  };

  const onDeleteClick = marker => {
    let markers = orderFlow.markerList?.filter(m => m.markerType === MarkerType.RE_CUTTING);
    markers = markers?.filter(m => m.id !== marker.id);
    saveOrUpdateMarkers(markers);
  };

  const onPrint = ({ layNo }) => {
    if (isEmpty(order) || isEmpty(orderFlow)) return;

    const options = {
      url: `orders/${id}/bundles`,
      method: "get",
      params: { flowId: orderFlow.id, layNo, action: "manual" }
    };
    mutate(options, {
      onSuccess: bundleSheet => {
        printBundleSheet({
          order,
          orderFlow,
          bundleSheet,
          tenant,
          flowInfoFields,
          i18nMessages,
          orderFields: orderFields
        });
        setDialogState({ open: false });
      }
    });
  };

  const columnsMarker = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(orderFlow)) return [];

    const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);

    const sizeBreakupList = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []);
    const haveSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup))?.length;

    let columns = [
      { dataKey: "sizeGroup", title: "Size Group" },
      { dataKey: "name", title: "Marker" },
      { dataKey: "noOfWays", title: "Way" },
      { dataKey: "groupName", title: "Group" },
      { dataKey: "layLength", title: "Lay Length" },
      { dataKey: "efficiency", title: "Efficiency" },
      { dataKey: "layNo", title: "Lay No" },
      { dataKey: "status", title: "Status" },
      { dataKey: "action", title: "Action" }
    ];

    if (!haveSizeGroup) {
      columns = columns.filter(c => c.dataKey !== "sizeGroup");
    }

    return columns;
  }, [order, orderFlow]);

  const rowsMarker = useDeepMemo(() => {
    if (isEmpty(orderFlow?.markerList)) return [];

    const markerList = orderFlow.markerList;
    const markers = markerList.filter(m => m.markerType === MarkerType.RE_CUTTING);
    const layList = !isEmpty(layslip) ? layslip[LayType.RECUTTING] : [];

    let rows = markers
      .map(m => {
        const lay = layList?.find(l => l.marker && l.marker.id === m.id) || {};
        let actions = [<ImageEye key="view" style={{ cursor: "pointer", width: 24 }} onClick={() => onViewClick(lay, layList)} />];
        if (!isEmpty(m.layLength)) {
          actions.push(<ContentCreate key="edit" style={{ cursor: "pointer", width: 24 }} onClick={() => onEditClick(lay, layList)} />);
        }
        if (isEmpty(lay.rowList)) {
          actions.push(<DeleteIcon key="delete" style={{ cursor: "pointer", width: 24 }} onClick={() => onDeleteClick(m)} />);
        }

        const sizeGroup = getDistinctValues(m.name?.split(", ").map(e => e.split("_")[0]));
        const markerName = m.name
          ?.split(", ")
          .map(e => e.split("_")[1])
          .join(", ");
        return {
          ...m,
          name: markerName,
          sizeGroup,
          layLength: m.layLength || "-",
          groupName: `${toFixed(m.width, 1)}CM${
            !isEmpty(m.warpPattern) && !isEmpty(m.weftPattern) ? `, L${toFixedSigned(m.warpPattern, 1)}%W${toFixedSigned(m.weftPattern, 1)}` : ""
          }`,
          layNo: lay.layNo,
          status: lay.state,
          action: actions
        };
      })
      .sort((a, b) => a.layNo - b.layNo);
    return rows;
  }, [order, orderFlow, layslip]);

  const rollList = useDeepMemo(() => {
    if (isEmpty(rollInfoList) || isEmpty(orderFlow)) return [];

    const groups = orderFlow?.groupList?.filter(g => g.type === GroupType.FINAL) || [];
    const rollList = rollInfoList.map(roll => {
      const group = groups?.find(g => g.id === roll.finalGroupId) || {};
      return {
        ...roll,
        rollNo: getRollNo(roll.rollNo),
        group,
        shrinkageValue: isEmpty(group?.shrinkageValue)
          ? ""
          : `${isEmpty(group?.patternNumber) ? "" : `${group.patternNumber} - `}L${toFixedSigned(group.warpPattern, 1)}%, W${toFixedSigned(
              group.weftPattern,
              1
            )}%`,
        groupName: getGroupName(group),
        available: Number(toFixed(roll.available)),
        reCutGarment: Number(toFixed(roll.reCutGarment))
      };
    });

    return rollList;
  }, [order, orderFlow, layslip, rollInfoList, styleColor]);

  const smallestML = useDeepMemo(() => {
    const markerList =
      orderFlow?.markerList
        ?.filter(m => m.markerType === MarkerType.REMNANT || m.markerType === MarkerType.RE_CUTTING)
        .filter(m => m.completed && m.length > 0) || [];
    return getSmallestML(order, orderFlow, styleColor, markerList);
  }, [order, orderFlow, styleColor]);

  const rowsRoll = useDeepMemo(() => {
    let rows = rollList
      .filter(ri => ri.reCutGarment > 0 || ri.available >= smallestML)
      .sort((a, b) => a.available - b.available)
      .map((r, idx) => ({ ...r, serialNo: idx + 1 }));
    if (rows?.length > 1) {
      let rowTotal = ["available", "reCutGarment"].reduce(
        (acc, key) => ({
          ...acc,
          [key]: rows.reduce((acc, roll) => acc + parseFloat(roll[key]), 0).toFixed(2)
        }),
        {}
      );
      rows.push({ ...rowTotal, shade: "Total", totalRow: true });
    }
    return rows;
  }, [rollList, smallestML]);

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "download") {
      const filename = `AVAILABLE_ROLLS`;

      const columnList = [
        { dataKey: "rollNo", type: "string", title: lodashGet(i18nMessages, `resources.rolls.fields.rollNo`, "ROLL NO") },
        { dataKey: "rollType", type: "string", title: lodashGet(i18nMessages, `resources.rolls.fields.rollType`, "Roll Type") },
        { dataKey: "color", type: "string", title: lodashGet(i18nMessages, `resources.rolls.fields.color`, "Style Color") },
        { dataKey: "length", type: "double", title: lodashGet(i18nMessages, `resources.rolls.fields.length`, "Length") },
        { dataKey: "width", type: "double", title: lodashGet(i18nMessages, `resources.rolls.fields.width`, "Width") },
        { dataKey: "warpShrPercent", type: "double", title: lodashGet(i18nMessages, `resources.rolls.fields.warpShrPercent`, "Warp Shrinkage (%)") },
        { dataKey: "weftShrPercent", type: "double", title: lodashGet(i18nMessages, `resources.rolls.fields.weftShrPercent`, "Weft Shrinkage (%)") },
        { dataKey: "csv", type: "string", title: lodashGet(i18nMessages, `resources.rolls.fields.csv`, "CSV") },
        { dataKey: "shade", type: "string", title: lodashGet(i18nMessages, `resources.rolls.fields.shade`, "Shade") },
        { dataKey: "available", type: "double", title: lodashGet(i18nMessages, `resources.rolls.fields.available`, "Available") },
        { dataKey: "groupName", type: "string", title: lodashGet(i18nMessages, `resources.rolls.fields.groupName`, "Group") }
      ].map((c, idx) => ({ ...c, colNo: idx + 1, title: c.title.toUpperCase() }));

      const rollList = rowsRoll.filter(r => r.shade !== "Total");

      const rowList = rollList.map((row, idx) => ({
        rowNo: idx + 1,
        cellList: columnList.map(c => ({
          dataKey: c.dataKey,
          value: row[c.dataKey]
        }))
      }));

      const options = {
        url: `export/excel`,
        method: "post",
        responseType: "arraybuffer",
        params: { filename },
        data: [{ sheetNo: 0, sheetName: "Available Rolls", autoSizeColumn: true, excelData: { columnList, rowList } }]
      };

      download(options, {
        onSuccess: response => {
          if (response.status === 200) {
            const header = response.headers["content-disposition"];
            var fileName = header ? header.match(/filename="(.+)"/)[1] : `${filename}.xlsx`;

            let blob = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
          }
          onActionComplete && onActionComplete(rows);
        }
      });
    }
  };

  const onPrintReport = () => {
    printGarmentRecuttingReport({
      order,
      orderFlow,
      color: styleColor?.split(DEFAULT_SEPARATOR)[0],
      rowsCutSummary,
      rowsRoll,
      layMap: layslip,
      tenant,
      flowInfoFields,
      i18nMessages: messages,
      orderFields: orderFields
    });
  };

  const feedbackCompleted = isFeedbackCompleted(orderFlow);

  const reCutMarkers = orderFlow?.markerList?.filter(m => m.markerType === MarkerType.RE_CUTTING) || [];

  return (
    <div>
      <PageHeader title="Garment Recutting" />

      <ManualMarkerDialog
        open={editing}
        order={order}
        orderFlow={orderFlow}
        onClose={() => setEditing(false)}
        onSubmit={saveOrUpdateMarkers}
        onActionComplete={() => setEditing(false)}
      />

      <LayDialog
        open={dialogState?.open || false}
        order={order}
        orderFlow={orderFlow}
        lay={dialogState.lay}
        deviationTypes={deviationTypes}
        layMap={layslip}
        onClose={() => setDialogState({ open: false })}
        onRefresh={() => setVersion(v => v + 1)}
        onPrint={onPrint}
      />

      <div className={classes.content}>
        {/* PO Details */}
        <MuiTable title="PO Details" toolbar columns={poInfoColumns} rows={poInfoRows} i18nKey="resources.orders.fields" />

        <div className={classes.mrTop}>
          <MuiTable
            key={version}
            title={translate("resources.rollPlan.title.cut_order", { _: "Cut Order" })}
            toolbar={true}
            searchable={true}
            pageable={true}
            searchKeys={["flowRef", "customerFlowRef", "style", "styleNo"]}
            idKey="key"
            parentIdKey="parentKey"
            columns={flowInfoColumns}
            rows={flowInfoRows}
            defaultExpanded={defaultExpanded}
            expandedColor={["#BFDDF8", "#7AB9F0"]}
            onRowClick={onRowClick}
            rowStyle={({ row }) => ({ backgroundColor: selectedKey === row.key ? "#BFDDF8" : "none" })}
            i18nKey="resources.grouping.fields.flowInfo"
          />
        </div>

        {!feedbackCompleted && (
          <Alert variant="filled" severity="warning" className={classes.mrTop}>
            <Typography variant="h6">Cutting not completed yet. Garment recutting can be done after complete feedback is recieved.</Typography>
          </Alert>
        )}

        {feedbackCompleted && (
          <div>
            {/** Cut Summary Card */}
            <Card className={classes.mrTop}>
              <CardHeader title="Cut Summary" />
              {styleColorList.length > 0 && <Divider />}
              {styleColorList.length > 0 && (
                <Tabs value={styleColorTabIdx} onChange={(e, idx) => setStyleColorTabIdx(idx)} variant="scrollable" scrollButtons="auto">
                  {styleColorList.map((styleColor, idx) => (
                    <Tab key={idx} label={styleCount > 1 ? styleColor.split(DEFAULT_SEPARATOR)[0] : styleColor.split(DEFAULT_SEPARATOR)[1]} />
                  ))}
                </Tabs>
              )}
              <CardContent style={{ padding: 0 }}>
                <PerfectScrollbar>
                  <Table
                    classes={{ table: classes.nowrapTable }}
                    columns={columnsCutSummary}
                    headerRows={headerRows}
                    rows={rowsCutSummary}
                    headerCellStyle={column => ({
                      borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                      borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                      backgroundColor: "#C6D0DA"
                    })}
                    cellStyle={({ column, record }) =>
                      record.name === "Total"
                        ? {
                            fontWeight: "bold",
                            borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                            borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                          }
                        : {
                            borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                            borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                          }
                    }
                  />
                </PerfectScrollbar>
              </CardContent>
            </Card>

            {/** Manual Plan Card */}
            <Card className={classes.mrTop}>
              <CardHeader title="Manual Plan" />
              <Divider />
              <PerfectScrollbar>
                <Table columns={columnsMarker} rows={rowsMarker} emptyMessage="Markers not available" />
              </PerfectScrollbar>
              {reCutMarkers.length === 0 && <Divider />}
              {!editing && orderFlow.stateValue <= OState.FEEDBACK_COMPLETED.stateValue && (
                <div className={classes.footer}>
                  <MuiButton color="primary" onClick={() => setEditing(true)}>
                    Edit
                  </MuiButton>
                </div>
              )}
            </Card>

            {/** Available Rolls */}
            <Card className={classes.mrTop}>
              <MuiTable
                title="Available Rolls"
                toolbar
                columns={columnsRoll}
                rows={rowsRoll}
                searchable
                searchKeys={["rollNo"]}
                footerActions={[{ name: "download", tooltip: "Download", options: { variant: "outlined" } }]}
                onFooterActionClick={onFooterActionClick}
              />
            </Card>
          </div>
        )}

        <PageFooter>
          {feedbackCompleted && (
            <Button
              className={classes.button}
              label="Bundle Sheet"
              variant="contained"
              color="primary"
              disabled={busy}
              onClick={() => onPrint({ layNo: -1 })}>
              <ActionPrint />
            </Button>
          )}

          {!editing && feedbackCompleted && (
            <React.Fragment>
              <Button className={classes.button} label="Recutting Report" variant="outlined" color={"primary"} onClick={onPrintReport}>
                <ActionPrint />
              </Button>
            </React.Fragment>
          )}
          <BackButton variant="outlined" color="primary" />
        </PageFooter>
      </div>
    </div>
  );
};

export default translate(GarmentRejectionView);
